import Phaser from 'phaser';
import { PVP_HULL_WORLD_UNITS } from '../pvp/ship-art';

/**
 * **O desenho dos seis golpes físicos dos bosses corpo a corpo** (gate V28I.BA).
 *
 * ## Nada aqui decide nada
 *
 * A autoridade manda a habilidade, a fase, o progresso, a etapa, a origem, a
 * direcção congelada, os pontos auxiliares e a geometria — alcance, meia-largura
 * e meio-ângulo — e isto desenha exactamente isso. **O desenho é o que magoa**:
 * a mesma forma que o servidor usou para decidir o acerto, sem uma segunda
 * descrição no cliente que pudesse discordar dela.
 *
 * O progresso é o da FASE em curso, calculado pelo servidor a partir de dois
 * tiques absolutos. Este ficheiro nunca conta o tempo dele: ele interpola sobre
 * o número que chegou, e uma trama perdida atrasa o desenho em vez de o
 * dessincronizar.
 *
 * ## Como se mantém barato sem tirar nada
 *
 * Um `Graphics` por boss, criado à entrada e destruído à saída — nunca um por
 * quadro e nunca um por etapa. As partículas saem de piscinas de tamanho fixo,
 * com duas texturas geradas uma vez. Nada é alocado dentro de um quadro, e nada
 * é reduzido, escondido, amostrado ou degradado por causa de desempenho.
 */

/** As seis habilidades, exactamente as do vocabulário fechado do servidor. */
export type BossStrikeAbility =
  /* Os seis golpes físicos (gate V28I.BA). */
  | 'stinger_lunge'
  | 'circular_judgment'
  | 'ember_roar'
  | 'chain_whirl'
  | 'twin_chains'
  | 'reaping_fan'
  /* As seis magias exclusivas dos conjuradores 1 a 6 (gate V28I.BC). */
  | 'crown_of_ashes'
  | 'blood_seal'
  | 'putrid_breath'
  | 'ocular_beam'
  | 'swarm_stance'
  | 'cross_of_lament'
  /* As cinco magias dos conjuradores 7, 8, 10, 14 e 17 (gate V28I.BE). */
  | 'hunger'
  | 'choir_of_faces'
  | 'rotary_fire'
  | 'five_maws'
  | 'hypnotic_dust'
  /* Os três golpes de pressão dos finais 18, 19 e 20 (gate V28I.BG). */
  | 'depth_bombardment'
  | 'choral_volley'
  | 'pack_bite';

/** Os três teleguiados inevitáveis. Um corpo discreto, e nunca um feixe. */
export type BossHomingAbility = 'abyssal_harpoon' | 'judgment_spear' | 'crown_gaze';

/** As três ultimates. */
export type BossUltimateAbility = 'abyssal_collapse' | 'sentence' | 'decree';

export type BossStrikePhase = 'windup' | 'telegraph' | 'active' | 'recovery';

export type BossStrikeShape = 'capsule' | 'ring' | 'disc' | 'sector' | 'cross' | 'wave';

/** O golpe em curso, tal como a autoridade o descreveu. */
export interface BossStrikeDraw {
  readonly ability: BossStrikeAbility;
  readonly phase: BossStrikePhase;
  /** O progresso DESTA fase, em milésimos. Do servidor, e nunca do relógio local. */
  readonly progressMilli: number;
  readonly stage: number;
  readonly shape: BossStrikeShape;
  readonly originX: number;
  readonly originY: number;
  readonly dirX: number;
  readonly dirY: number;
  /**
   * **Os pontos congelados, em pares.** `P1` e `P2` das Duas Correntes, os
   * quatro círculos do Bombardeio, ou os sete fins da Salva Coral.
   */
  readonly points: readonly number[];
  readonly reachMu: number;
  /** O raio INTERIOR de uma onda. Zero nas outras formas. */
  readonly innerMu: number;
  readonly halfWidthMu: number;
  /** O meio-ângulo em basis points da tangente. Zero fora de um sector. */
  readonly sectorTanBps: number;
}

/** Uma brasa no chão, com os dois relógios da sala. */
/** Uma esfera de enxame em voo. Um corpo próprio, com rasto e impacto próprios. */
export interface BossSwarmDraw {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
}

/**
 * **Um obus do Bombardeio de Profundidade em voo** (gate V28I.CF.1A).
 *
 * A posição é a que a cena DESENHA — a mesma interpolação de todos os corpos em
 * voo, já em unidades de mundo —, e o resto é o que a sala disse: o passo do
 * próximo tique e o centro do círculo onde ele cai, em milli-unidades.
 */
export interface BossShellDraw {
  readonly id: number;
  readonly drawnX: number;
  readonly drawnY: number;
  /** O passo do próximo tique. Zero quando parou no destino à espera do impacto. */
  readonly vx: number;
  readonly vy: number;
  readonly targetX: number;
  readonly targetY: number;
}

/**
 * **Um teleguiado no ar**, tal como a autoridade o descreveu.
 *
 * Um corpo, com posição, rumo e raio. Nem o dano nem o alvo chegam aqui, e é
 * deliberado: não há nada a decidir sobre uma coisa que não se pode travar.
 */
export interface BossHomingDraw {
  readonly id: number;
  readonly ability: BossHomingAbility;
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
}

/** Um núcleo rúnico da Sentença: uma coisa com vida, que se pode partir. */
export interface BossCoreDraw {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
}

/**
 * **A ultimate em curso**, com o corpo de quem a lança e a banda que magoa.
 *
 * `involved` é a resposta sobre ESTE piloto. Ela muda a intensidade do desenho
 * e mais nada — quem não está envolvido continua a ver o que está a acontecer,
 * porque a luta é dos outros também.
 */
export interface BossUltimateDraw {
  readonly id: string;
  readonly ability: BossUltimateAbility;
  readonly phase: 'telegraph' | 'channel' | 'resolve';
  readonly progressMilli: number;
  readonly interruptMilli: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly innerMu: number;
  readonly outerMu: number;
  readonly involved: boolean;
}

export interface BossEmberDraw {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  /** `disc` (uma brasa) ou `sector` (a névoa pútrida). */
  readonly shape: 'disc' | 'sector';
  readonly dirX: number;
  readonly dirY: number;
  readonly sectorTanBps: number;
  /** A lentidão que ela impõe. Zero é "não abranda", e o desenho di-lo. */
  readonly slowBps: number;
  readonly bornAtTick: number;
  readonly expiresAtTick: number;
}

/** As cores aprovadas. Quentes, porque o corpo a corpo é quente. */
const TELEGRAPH_TINT = 0xff8a3c;
const ACTIVE_TINT = 0xffd66b;
const EMBER_TINT = 0xff5a1e;
const CHAIN_TINT = 0xffc27a;

/**
 * **A cor de cada magia, lida da ARTE de cada boss** (gate V28I.BC).
 *
 * Escolhidas depois de abrir os seis PNG de `assets-source/bosses/`, e não
 * inventadas: o boss 1 é um rei coroado de negro e ouro com chamas cor de brasa
 * nos chifres; o 2 está sentado num sigilo vermelho de sangue que já tem anéis
 * concêntricos e glifos radiais; o 3 arde em chama verde-ácido; o 4 é um olho
 * único de íris escarlate; o 5 traz esferas escuras avermelhadas a orbitá-lo; e
 * o 6 é uma face branca de osso numa cruz de tentáculos carmesim.
 *
 * Nenhum deles ganhou nome oficial, e nenhuma arte foi alterada.
 */
interface SpellTint {
  readonly warn: number;
  readonly hot: number;
}

const MIASMA_TINT: SpellTint = { warn: 0x7ad42a, hot: 0x9cff2e };

/** As esferas do boss 5: escuras com halo carmesim, como a arte delas. */
const SWARM_CORE_TINT = 0xff2a3c;
const SWARM_HALO_TINT = 0x8c1420;

const SPELL_TINT: Readonly<Record<string, SpellTint | undefined>> = {
  crown_of_ashes: { warn: 0xff8a2c, hot: 0xffc25a },
  blood_seal: { warn: 0xd6142c, hot: 0xff1e34 },
  putrid_breath: { warn: 0x7ad42a, hot: 0x9cff2e },
  ocular_beam: { warn: 0xd63a1e, hot: 0xff4a2a },
  swarm_stance: { warn: 0xd6202c, hot: 0xff2a3c },
  cross_of_lament: { warn: 0xc4b8bc, hot: 0xf2eaea },
  /*
   * As cinco do gate V28I.BE, depois de abrir os PNG: o boss 7 é um infante
   * pálido com um **buraco negro-violeta no peito**; o 8 é uma catedral coberta
   * de faces que gritam, em luz violeta; o 10 é um mecha de marfim com **dois
   * canhões rotativos** de bocas violeta; o 14 é uma **hidra dourada de cinco
   * cabeças** com engrenagens; e o 17 é uma mariposa espectral azul com olhos
   * nas asas e motes azuis a flutuar.
   */
  hunger: { warn: 0x7a4a8c, hot: 0xe8dfe4 },
  choir_of_faces: { warn: 0x7a1ecc, hot: 0xb44aff },
  rotary_fire: { warn: 0x7c3aed, hot: 0xa855f7 },
  five_maws: { warn: 0xd4a017, hot: 0xffc94a },
  hypnotic_dust: { warn: 0x1e5cff, hot: 0x2ea8ff },
  /*
   * Os três finais, depois de abrir `boss18.png`, `boss19.png` e `boss20.png`:
   * o 18 é um cefalópode biomecânico **preto com um núcleo branco** e quatro
   * bocas de canhão; o 19 é um mecha serafim **branco-azul** com asas de lâminas
   * e um halo; o 20 é uma rainha coroada sobre uma montaria de cinco cabeças,
   * em **violeta escuro** com um halo de olhos.
   */
  depth_bombardment: { warn: 0x5a7a8c, hot: 0xeaf4ff },
  choral_volley: { warn: 0x2a5cff, hot: 0x8ec4ff },
  pack_bite: { warn: 0x6b3fa0, hot: 0xa06cff },
};

/** Os três teleguiados, cada um com a cor do boss que o lança. */
const HOMING_TINT: Readonly<Record<BossHomingAbility, SpellTint>> = {
  abyssal_harpoon: { warn: 0xa8bccc, hot: 0xeaf4ff },
  judgment_spear: { warn: 0x2a5cff, hot: 0xf4f8ff },
  crown_gaze: { warn: 0x6b3fa0, hot: 0xa06cff },
};

/** As três ultimates. O anúncio é a cor fria; a execução é a quente. */
const ULTIMATE_TINT: Readonly<Record<BossUltimateAbility, SpellTint>> = {
  abyssal_collapse: { warn: 0x1e2a34, hot: 0xeaf4ff },
  sentence: { warn: 0x2a5cff, hot: 0xf4f8ff },
  decree: { warn: 0x6b3fa0, hot: 0xa06cff },
};

/** Um núcleo rúnico: pedra azul-branca, com a barra de vida por cima. */
const CORE_TINT = 0x8ec4ff;
const CORE_BAR_TINT = 0xff4d5e;

/** Quantas presas a mordida da matilha desenha. Cinco, uma por cabeça. */
const PACK_FANGS = 5;

/** O vazio da Fome. Preto-violeta, como o buraco no peito do boss 7. */
const VOID_TINT = 0x2a1030;

/** As balas do Fogo Rotativo: núcleo violeta claro, halo escuro. */
const ROTARY_CORE_TINT = 0xc4a0ff;
const ROTARY_HALO_TINT = 0x5b21b6;
/*
 * **Os obuses do Bombardeio de Profundidade** (gate V28I.CF.1A).
 *
 * Três camadas por obus, por ordem de brilho:
 * - o NÚCLEO, branco, curto e alongado no sentido do voo — o pixel mais claro;
 * - a ENERGIA, um halo frio mais largo e pouco opaco, esticado ao longo do rumo;
 * - o RESÍDUO, um rasto de faíscas mais pequeno e mais fraco do que o corpo.
 *
 * A linha até ao círculo diz a direcção e o destino com pouco contraste e
 * apaga-se à medida que o obus se aproxima; perto do fim o nariz diz o rumo e a
 * mira no centro fica. O impacto é o momento mais forte: um clarão curto no ponto
 * exacto, uma onda pequena e poucos fragmentos, por baixo do casco.
 *
 * Os tamanhos são de APRESENTAÇÃO, em diâmetros de casco, e nunca saem de um
 * número de equilíbrio. Cada camada de sinal tem um mínimo em píxeis de ecrã: no
 * zoom mais afastado os quatro corpos continuam quatro traços distintos, e nada
 * é escondido, agrupado ou reduzido por distância.
 */
const SHELL_CORE_TINT = 0xffffff;
const SHELL_HALO_TINT = 0x6fa8d8;
const SHELL_TRAIL_TINT = 0x4f86b8;
const SHELL_GUIDE_TINT = 0x7fb0d6;
/** O núcleo: comprimento e espessura em diâmetros de casco, e os mínimos no ecrã. */
const SHELL_CORE_LENGTH_HULLS = 0.62;
const SHELL_CORE_WIDTH_HULLS = 0.2;
const SHELL_CORE_MIN_LENGTH_PX = 12;
const SHELL_CORE_MIN_WIDTH_PX = 5;
/** O halo, em múltiplos do núcleo: mais largo, esticado no rumo, e pouco opaco. */
const SHELL_HALO_LENGTH = 2.6;
const SHELL_HALO_WIDTH = 1.5;
const SHELL_HALO_ALPHA = 0.26;
/** Longe, o halo pesa um pouco mais e nunca fica abaixo de 30 × 18 px no ecrã. */
const SHELL_HALO_FAR_ALPHA = 0.18;
const SHELL_HALO_MIN_LENGTH_PX = 30;
const SHELL_HALO_MIN_WIDTH_PX = 18;
/**
 * **A sombra**: um contorno escuro por baixo do núcleo, em mistura NORMAL.
 *
 * É ela que separa um núcleo branco das luzes brancas da arte do boss 18 quando
 * os dois se cruzam — mais brilho não o faria, porque branco sobre branco em
 * mistura aditiva continua branco.
 */
const SHELL_SHADE_TINT = 0x050b16;
/** Perto ela quase não se nota; longe, onde o corpo cruza a arte clara, pesa mais. */
const SHELL_SHADE_NEAR_ALPHA = 0.3;
const SHELL_SHADE_FAR_ALPHA = 0.6;
const SHELL_SHADE_LENGTH = 1.5;
const SHELL_SHADE_WIDTH = 2.2;
/** O zoom mais afastado do mundo aberto (ADR 0125): a referência de "longe". */
const SHELL_FARTHEST_ZOOM = 0.3125;
/** O rasto: mais pequeno, mais fraco e mais curto do que qualquer camada do corpo. */
const SHELL_TRAIL_SCALE = 0.45;
const SHELL_TRAIL_ALPHA = 0.35;
const SHELL_TRAIL_LIFE_MS = 260;
/** A linha até ao círculo: fina, de pouco contraste, e a apagar-se no fim do voo. */
const SHELL_GUIDE_SCREEN_PX = 1;
const SHELL_GUIDE_ALPHA = 0.2;
const SHELL_DASH_SCREEN_PX = 10;
/** A mira no centro do círculo: discreta, e presente até ao impacto. */
const SHELL_MARK_ALPHA = 0.32;
const SHELL_MARK_MIN_PX = 6;
/** Um tecto de traços por linha: acima dele os traços alongam, e a linha fica inteira. */
const SHELL_DASHES_MAX = 64;
/** O impacto: clarão, onda e fragmentos, todos curtos. */
const SHELL_FLASH_MS = 150;
const SHELL_WAVE_MS = 260;
const SHELL_FRAGMENTS = 5;
const SHELL_FRAGMENT_LIFE_MS = 240;
/** O clarão e a onda do tamanho de um casco, e não do círculo, com mínimos no ecrã. */
const SHELL_FLASH_HULLS = 1.1;
const SHELL_WAVE_HULLS = 1.6;
const SHELL_FLASH_MIN_PX = 22;
const SHELL_WAVE_MIN_PX = 30;
/** Impactos em simultâneo na piscina: três finais com quatro obuses cada. */
const SHELL_IMPACT_MAX = 12;
/** O círculo: preenchimento quase transparente, e a borda precisa mas suave. */
const SHELL_CIRCLE_FILL_ALPHA = 0.035;
const SHELL_CIRCLE_HOT_FILL_ALPHA = 0.08;
const SHELL_CIRCLE_EDGE_PX = 1.5;
const SHELL_CIRCLE_SOFT_PX = 7;
const SHELL_CIRCLE_SOFT_ALPHA = 0.07;

/** O pó do boss 17, frio e depois incandescente. */
const DUST_COLD_TINT = 0x2ea8ff;
const DUST_HOT_TINT = 0xffe08a;

/** Quantos glifos radiais o Selo de Sangue desenha. Doze, e nunca menos. */
const SEAL_GLYPHS = 12;

/** Quantos anéis concêntricos ele tem. Três, como a arte do boss 2. */
const SEAL_RINGS = 3;

const SPARK_TEXTURE = 'open-world-strike-spark';
const GLOW_TEXTURE = 'open-world-strike-glow';
const SPARK_PX = 12;
const GLOW_PX = 64;
/** Um anel branco, gerado uma vez e colorido por `setTint`: a onda de um impacto. */
const RING_TEXTURE = 'open-world-strike-ring';
const RING_PX = 64;

/** As piscinas. Provisórias, e dimensionadas para três bosses em execução. */
const SPARK_MAX = 192;
const EMBER_MOTE_MAX = 160;
const SPARK_LIFE_MS = 420;

/** Quantas linhas de atracção a Fome desenha. Doze, e sempre doze. */
const VOID_LINES = 12;

/** Quantas espadas orbitam a Sentença. Seis, e são apresentação de UM golpe. */
const JUDGMENT_SWORDS = 6;

interface Spark {
  readonly image: Phaser.GameObjects.Image;
  bornMs: number;
  vx: number;
  vy: number;
  fromScale: number;
  toScale: number;
  tint: number;
  /** A vida e a opacidade inicial desta faísca. O rasto de um obus é mais fraco e mais curto. */
  lifeMs: number;
  alpha: number;
}

/** Como uma faísca nasce, quando não é a de sempre. */
interface SparkStyle {
  readonly scale?: number;
  readonly alpha?: number;
  readonly lifeMs?: number;
  readonly depth?: number;
}

interface StrikeView {
  readonly shape: Phaser.GameObjects.Graphics;
  readonly swords: Phaser.GameObjects.Image[];
  lastStage: number;
  lastPhase: BossStrikePhase | null;
}

interface EmberView {
  readonly glow: Phaser.GameObjects.Image;
  readonly core: Phaser.GameObjects.Image;
}

/** Um núcleo rúnico: o halo, a pedra, e a barra de vida dela. */
interface CoreView {
  readonly glow: Phaser.GameObjects.Image;
  readonly stone: Phaser.GameObjects.Image;
  readonly bar: Phaser.GameObjects.Graphics;
}

/** Um obus no ecrã: o halo, a sombra, o núcleo, e o que é preciso para decidir a saída. */
interface ShellView {
  readonly glow: Phaser.GameObjects.Image;
  readonly shade: Phaser.GameObjects.Image;
  readonly core: Phaser.GameObjects.Image;
  /** O centro do círculo, em unidades de mundo. */
  targetX: number;
  targetY: number;
  /** A distância ao centro a que uma saída é a chegada, e não um cancelamento. */
  arrival: number;
  /** O último rumo conhecido: parado no destino, o obus não perde o nariz. */
  rotation: number;
  /** A distância ao destino quando o corpo apareceu: a linha apaga-se pela fracção que falta. */
  startDistance: number;
}

/** Um impacto de obus na piscina: o clarão, o ponto branco e a onda. */
interface ImpactView {
  readonly flash: Phaser.GameObjects.Image;
  readonly spark: Phaser.GameObjects.Image;
  readonly wave: Phaser.GameObjects.Image;
  bornMs: number;
  flashSize: number;
  waveSize: number;
}

interface TextureMaker {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(
      config: { x: number; y: number },
      addToScene: boolean,
    ): {
      fillStyle(color: number, alpha?: number): unknown;
      fillCircle(x: number, y: number, radius: number): unknown;
      lineStyle(width: number, color: number, alpha?: number): unknown;
      strokeCircle(x: number, y: number, radius: number): unknown;
      generateTexture(key: string, width?: number, height?: number): unknown;
      destroy(): void;
    };
  };
}

/** Duas texturas brancas, geradas uma vez e coloridas por `setTint`. */
function makeStrikeTextures(scene: TextureMaker): void {
  if (!scene.textures.exists(RING_TEXTURE)) {
    /* Um traço nítido com uma faixa ténue por dentro: a onda lê-se sem ser um disco. */
    const ring = scene.make.graphics({ x: 0, y: 0 }, false);
    ring.lineStyle(6, 0xffffff, 0.22);
    ring.strokeCircle(RING_PX / 2, RING_PX / 2, RING_PX / 2 - 6);
    ring.lineStyle(2, 0xffffff, 1);
    ring.strokeCircle(RING_PX / 2, RING_PX / 2, RING_PX / 2 - 3);
    ring.generateTexture(RING_TEXTURE, RING_PX, RING_PX);
    ring.destroy();
  }
  if (!scene.textures.exists(SPARK_TEXTURE)) {
    const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(SPARK_PX / 2, SPARK_PX / 2, SPARK_PX / 2 - 1);
    graphics.generateTexture(SPARK_TEXTURE, SPARK_PX, SPARK_PX);
    graphics.destroy();
  }
  if (scene.textures.exists(GLOW_TEXTURE)) {
    return;
  }
  /*
   * Um halo por camadas concêntricas: uma só textura, colorida por `setTint`,
   * e não uma por cor. Cores próprias partem o batching do Phaser.
   */
  const glow = scene.make.graphics({ x: 0, y: 0 }, false);
  const steps = 10;
  for (let step = steps; step > 0; step -= 1) {
    glow.fillStyle(0xffffff, 0.09);
    glow.fillCircle(GLOW_PX / 2, GLOW_PX / 2, (GLOW_PX / 2) * (step / steps));
  }
  glow.generateTexture(GLOW_TEXTURE, GLOW_PX, GLOW_PX);
  glow.destroy();
}

export class OpenWorldBossStrikeFx {
  private readonly views = new Map<string, StrikeView>();
  private readonly embers = new Map<number, EmberView>();
  private readonly swarm = new Map<number, EmberView>();
  private readonly rotary = new Map<number, EmberView>();
  /** Um obus por identidade, e as linhas até aos círculos num único desenho. */
  private readonly shells = new Map<number, ShellView>();
  private shellGuides: Phaser.GameObjects.Graphics | null = null;
  /** Os obuses deste quadro, num conjunto reutilizado. */
  private readonly shellsAlive = new Set<number>();
  /** A piscina dos impactos de obus, com tecto. Nunca um objecto novo por impacto depois de cheia. */
  private readonly impacts: ImpactView[] = [];
  /** Um teleguiado por identidade, e um núcleo por identidade. Nunca partilhados. */
  private readonly homing = new Map<number, EmberView>();
  private readonly cores = new Map<number, CoreView>();
  /** O desenho de uma ultimate por boss. O anel, o núcleo e o progresso. */
  private readonly ultimates = new Map<string, Phaser.GameObjects.Graphics>();
  private readonly sparks: Spark[] = [];
  /** A dispersão decorativa. Um contador, e nunca `Math.random`. */
  private scatter = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    makeStrikeTextures(scene as unknown as TextureMaker);
  }

  /**
   * **Um quadro.** Desenha os avisos, as execuções e as brasas.
   *
   * `tick` é o relógio AUTORITATIVO da trama: as brasas medem-se contra ele, e
   * não contra `nowMs`, que só governa a vida das partículas decorativas.
   */
  update(
    strikes: readonly { readonly id: string; readonly strike: BossStrikeDraw | null }[],
    embers: readonly BossEmberDraw[],
    swarm: readonly BossSwarmDraw[],
    rotary: readonly BossSwarmDraw[],
    shells: readonly BossShellDraw[],
    homing: readonly BossHomingDraw[],
    cores: readonly BossCoreDraw[],
    ultimates: readonly BossUltimateDraw[],
    tick: number,
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();
    for (const entry of strikes) {
      if (entry.strike === null) {
        continue;
      }
      alive.add(entry.id);
      this.drawStrike(this.viewFor(entry.id), entry.strike, nowMs, worldUnits);
    }
    for (const [key, view] of this.views) {
      if (alive.has(key)) {
        continue;
      }
      view.shape.destroy();
      for (const sword of view.swords) {
        sword.destroy();
      }
      this.views.delete(key);
    }

    this.drawEmbers(embers, tick, nowMs, worldUnits);
    this.drawSwarm(swarm, nowMs, worldUnits);
    this.drawRotary(rotary, nowMs, worldUnits);
    this.drawShellBodies(shells, nowMs, worldUnits);
    this.drawHoming(homing, nowMs, worldUnits);
    this.drawCores(cores, nowMs, worldUnits);
    this.drawUltimates(ultimates, worldUnits);
    this.stepImpacts(nowMs);
    this.stepSparks(nowMs);
  }

  /** Tudo o que este sistema pôs no ecrã desaparece. Sem restos, e sem fugas. */
  destroy(): void {
    for (const view of this.views.values()) {
      view.shape.destroy();
      for (const sword of view.swords) {
        sword.destroy();
      }
    }
    this.views.clear();
    for (const view of this.embers.values()) {
      view.glow.destroy();
      view.core.destroy();
    }
    this.embers.clear();
    for (const view of this.swarm.values()) {
      view.glow.destroy();
      view.core.destroy();
    }
    this.swarm.clear();
    for (const view of this.rotary.values()) {
      view.glow.destroy();
      view.core.destroy();
    }
    this.rotary.clear();
    for (const view of this.shells.values()) {
      view.glow.destroy();
      view.shade.destroy();
      view.core.destroy();
    }
    this.shells.clear();
    this.shellGuides?.destroy();
    this.shellGuides = null;
    this.shellsAlive.clear();
    for (const impact of this.impacts) {
      impact.flash.destroy();
      impact.spark.destroy();
      impact.wave.destroy();
    }
    this.impacts.length = 0;
    for (const view of this.homing.values()) {
      view.glow.destroy();
      view.core.destroy();
    }
    this.homing.clear();
    for (const view of this.cores.values()) {
      view.glow.destroy();
      view.stone.destroy();
      view.bar.destroy();
    }
    this.cores.clear();
    for (const shape of this.ultimates.values()) {
      shape.destroy();
    }
    this.ultimates.clear();
    for (const spark of this.sparks) {
      spark.image.destroy();
    }
    this.sparks.length = 0;
  }

  private viewFor(id: string): StrikeView {
    const existing = this.views.get(id);
    if (existing !== undefined) {
      return existing;
    }
    const shape = this.scene.add.graphics();
    shape.setDepth(this.depth);
    shape.setBlendMode(Phaser.BlendModes.ADD);
    const swords: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < JUDGMENT_SWORDS; index += 1) {
      const sword = this.scene.add.image(0, 0, SPARK_TEXTURE);
      sword.setDepth(this.depth + 1);
      sword.setBlendMode(Phaser.BlendModes.ADD);
      sword.setVisible(false);
      swords.push(sword);
    }
    const created: StrikeView = { shape, swords, lastStage: -1, lastPhase: null };
    this.views.set(id, created);
    return created;
  }

  /**
   * **O aviso e a execução de um golpe.**
   *
   * O aviso cresce em intensidade ao longo da fase — é isso que dá a leitura do
   * tempo que falta — e a execução desenha a forma cheia. A geometria é a
   * autoritativa em todas as fases: um aviso mais pequeno do que o golpe seria
   * uma armadilha, e um maior faria fugir de nada.
   */
  private drawStrike(
    view: StrikeView,
    strike: BossStrikeDraw,
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const shape = view.shape;
    shape.clear();
    for (const sword of view.swords) {
      sword.setVisible(false);
    }

    const ox = worldUnits(strike.originX);
    const oy = worldUnits(strike.originY);
    const reach = worldUnits(strike.reachMu);
    const half = worldUnits(strike.halfWidthMu);
    const progress = Math.max(0, Math.min(1, strike.progressMilli / 1_000));
    const telegraph = strike.phase === 'telegraph' || strike.phase === 'windup';
    const palette = SPELL_TINT[strike.ability];
    const tint =
      palette === undefined
        ? telegraph
          ? TELEGRAPH_TINT
          : ACTIVE_TINT
        : telegraph
          ? palette.warn
          : palette.hot;
    /* O aviso acende à medida que o tempo passa; a execução entra cheia. */
    const alpha = telegraph ? 0.18 + progress * 0.34 : 0.62;
    const angle = Math.atan2(strike.dirY, strike.dirX);

    if (strike.reachMu <= 0) {
      /*
       * Uma etapa sem dano: o intervalo entre as duas maças, e a janela de fuga
       * entre a segunda e a terceira varrida. Não se desenha perigo nenhum,
       * porque não há nenhum — é essa ausência que faz a janela ser legível.
       */
      view.lastStage = strike.stage;
      view.lastPhase = strike.phase;
      return;
    }

    switch (strike.shape) {
      case 'capsule':
        if (strike.ability === 'choral_volley') {
          /* As sete lâminas são desenhadas por `drawFeathers`, e não uma só. */
          break;
        }
        this.drawCapsule(shape, strike, ox, oy, reach, half, angle, tint, alpha, worldUnits);
        break;
      case 'ring':
        this.drawRing(view, ox, oy, reach, half, progress, telegraph, tint, alpha);
        break;
      case 'disc':
        if (strike.ability === 'depth_bombardment') {
          /* Os quatro círculos são desenhados por `drawShells`, e não um só. */
          break;
        }
        shape.lineStyle(Math.max(2, half || 4), tint, alpha + 0.2);
        shape.strokeCircle(ox, oy, reach);
        shape.fillStyle(tint, alpha * 0.35);
        shape.fillCircle(ox, oy, reach);
        break;
      case 'sector':
        this.drawSector(shape, ox, oy, reach, angle, strike.sectorTanBps, tint, alpha);
        break;
      case 'cross':
        this.drawCross(shape, strike, ox, oy, half, tint, alpha, worldUnits);
        break;
      case 'wave': {
        /*
         * **A banda que magoa, e só ela.**
         *
         * O interior e o exterior vêm da autoridade: desenhar um disco cheio
         * mentiria sobre ficar entre duas ondas, que é a resposta inteira a esta
         * habilidade.
         */
        const inner = worldUnits(strike.innerMu);
        shape.lineStyle(Math.max(3, reach - inner), tint, alpha);
        shape.strokeCircle(ox, oy, (reach + inner) / 2);
        shape.lineStyle(2, tint, alpha + 0.3);
        shape.strokeCircle(ox, oy, reach);
        shape.strokeCircle(ox, oy, inner);
        break;
      }
    }

    if (strike.ability === 'depth_bombardment') {
      this.drawShells(shape, strike, reach, telegraph, tint, alpha, worldUnits);
    }

    if (strike.ability === 'choral_volley') {
      this.drawFeathers(shape, strike, ox, oy, half, tint, alpha, worldUnits);
    }

    if (strike.ability === 'pack_bite') {
      this.drawFangs(shape, ox, oy, reach, angle, strike.sectorTanBps, tint, alpha);
    }

    if (strike.ability === 'hunger') {
      this.drawVoid(shape, ox, oy, reach, progress, telegraph, tint, alpha);
    }

    if (strike.ability === 'blood_seal') {
      this.drawSeal(shape, ox, oy, reach, progress, telegraph, tint, alpha);
    }

    /* A entrada numa etapa nova acende faíscas na frente do golpe. */
    if (view.lastStage !== strike.stage || view.lastPhase !== strike.phase) {
      /*
       * O Bombardeio não acende esta rajada (gate V28I.CF.1A). "A frente do golpe"
       * dele não é um círculo nenhum, e as faíscas pareceriam um segundo impacto
       * no sítio errado. O impacto dele é o do obus, no centro exacto do círculo
       * que cobra.
       */
      if (strike.phase === 'active' && strike.ability !== 'depth_bombardment') {
        this.burst(
          ox + Math.cos(angle) * reach * 0.6,
          oy + Math.sin(angle) * reach * 0.6,
          nowMs,
          tint,
        );
      }
      view.lastStage = strike.stage;
      view.lastPhase = strike.phase;
    }
  }

  /** A cápsula: o corredor da estocada, e o varrimento de uma maça. */
  private drawCapsule(
    shape: Phaser.GameObjects.Graphics,
    strike: BossStrikeDraw,
    ox: number,
    oy: number,
    reach: number,
    half: number,
    angle: number,
    tint: number,
    alpha: number,
    worldUnits: (mu: number) => number,
  ): void {
    const width = Math.max(2, half * 2);
    if (strike.ability === 'twin_chains' && strike.points.length >= 4) {
      /* As duas correntes, do boss a `P1` e de `P1` a `P2`. Físicas, e não magia. */
      const p1x = worldUnits(strike.points[0] ?? 0);
      const p1y = worldUnits(strike.points[1] ?? 0);
      const p2x = worldUnits(strike.points[2] ?? 0);
      const p2y = worldUnits(strike.points[3] ?? 0);
      const first = strike.stage <= 1;
      shape.lineStyle(width, CHAIN_TINT, first ? alpha : alpha * 0.35);
      shape.lineBetween(ox, oy, p1x, p1y);
      shape.lineStyle(width, CHAIN_TINT, first ? alpha * 0.35 : alpha);
      shape.lineBetween(p1x, p1y, p2x, p2y);
      shape.fillStyle(tint, alpha);
      shape.fillCircle(p1x, p1y, half);
      shape.fillCircle(p2x, p2y, half);
      return;
    }
    const ex = ox + Math.cos(angle) * reach;
    const ey = oy + Math.sin(angle) * reach;
    shape.lineStyle(width, tint, alpha);
    shape.lineBetween(ox, oy, ex, ey);
    /* As duas pontas arredondadas: é o que faz a linha ser a cápsula real. */
    shape.fillStyle(tint, alpha * 0.7);
    shape.fillCircle(ox, oy, half);
    shape.fillCircle(ex, ey, half);
  }

  /**
   * **O vazio da Fome: o buraco, o limite e as linhas de atracção.**
   *
   * Uma linha **por casco** dentro do raio — a cena não sabe quais, portanto
   * desenha-as em raios iguais à volta do limite, que é o que a autoridade lhe
   * disse existir. Nada aqui decide quem é puxado.
   */
  private drawVoid(
    shape: Phaser.GameObjects.Graphics,
    ox: number,
    oy: number,
    reach: number,
    progress: number,
    telegraph: boolean,
    tint: number,
    alpha: number,
  ): void {
    /* O limite do puxão: a linha que se pode atravessar. */
    shape.lineStyle(3, tint, 0.5);
    shape.strokeCircle(ox, oy, reach);
    /* O vazio no peito, a abrir com o aviso. */
    const mouth = Math.max(4, reach * 0.08 * (telegraph ? 0.4 + progress * 0.6 : 1));
    shape.fillStyle(VOID_TINT, 0.95);
    shape.fillCircle(ox, oy, mouth);
    shape.lineStyle(3, tint, 0.9);
    shape.strokeCircle(ox, oy, mouth);
    /*
     * As linhas de atracção, a espiralar para dentro. Doze, sempre as mesmas, e
     * o passo delas vem do progresso da autoridade — nunca do relógio local.
     */
    const spin = progress * Math.PI;
    for (let line = 0; line < VOID_LINES; line += 1) {
      const angle = (line / VOID_LINES) * Math.PI * 2 + spin;
      const from = reach * (telegraph ? 1 : 1 - progress * 0.7);
      shape.lineStyle(2, tint, alpha * 0.8);
      shape.lineBetween(
        ox + Math.cos(angle) * from,
        oy + Math.sin(angle) * from,
        ox + Math.cos(angle + 0.35) * mouth,
        oy + Math.sin(angle + 0.35) * mouth,
      );
    }
  }

  /**
   * **A cruz de quatro faixas.** Elas acendem juntas e disparam juntas.
   *
   * As diagonais ficam claramente vazias: é isso que faz delas as zonas de
   * sobrevivência, e não uma convenção que alguém tem de saber.
   */
  private drawCross(
    shape: Phaser.GameObjects.Graphics,
    strike: BossStrikeDraw,
    ox: number,
    oy: number,
    half: number,
    tint: number,
    alpha: number,
    worldUnits: (mu: number) => number,
  ): void {
    const width = Math.max(3, half * 2);
    for (let band = 0; band + 1 < strike.points.length; band += 2) {
      const ex = worldUnits(strike.points[band] ?? 0);
      const ey = worldUnits(strike.points[band + 1] ?? 0);
      shape.lineStyle(width, tint, alpha);
      shape.lineBetween(ox, oy, ex, ey);
      /* Um clarão na ponta de cada faixa, como a arte do boss 6 já sugere. */
      shape.fillStyle(tint, alpha * 0.8);
      shape.fillCircle(ex, ey, Math.max(3, half));
    }
    shape.fillStyle(tint, alpha * 0.6);
    shape.fillCircle(ox, oy, Math.max(4, half * 1.2));
  }

  /**
   * **O sigilo do Selo de Sangue**, desenhado como a arte do boss 2 o desenha:
   * três anéis concêntricos e doze glifos radiais, a completar-se.
   *
   * Procedural e sem textura própria: é geometria, e desenhá-la custa o mesmo
   * traço que qualquer outra. Os doze glifos são doze, sempre.
   */
  private drawSeal(
    shape: Phaser.GameObjects.Graphics,
    ox: number,
    oy: number,
    reach: number,
    progress: number,
    telegraph: boolean,
    tint: number,
    alpha: number,
  ): void {
    const drawn = telegraph ? progress : 1;
    for (let ring = 1; ring <= SEAL_RINGS; ring += 1) {
      shape.lineStyle(2, tint, alpha * 0.8);
      shape.strokeCircle(ox, oy, (reach * ring) / SEAL_RINGS);
    }
    for (let glyph = 0; glyph < SEAL_GLYPHS; glyph += 1) {
      const done = (glyph + 1) / SEAL_GLYPHS;
      if (done > drawn) {
        break;
      }
      const spin = (glyph / SEAL_GLYPHS) * Math.PI * 2;
      const inner = reach * 0.35;
      shape.lineStyle(3, tint, alpha + 0.2);
      shape.lineBetween(
        ox + Math.cos(spin) * inner,
        oy + Math.sin(spin) * inner,
        ox + Math.cos(spin) * reach,
        oy + Math.sin(spin) * reach,
      );
    }
    /* O preenchimento sobe com o desenho: é o relógio que se vê. */
    shape.fillStyle(tint, alpha * 0.3 * drawn);
    shape.fillCircle(ox, oy, reach);
  }

  /** O anel da Sentença, e as seis espadas que são a apresentação dele. */
  private drawRing(
    view: StrikeView,
    ox: number,
    oy: number,
    outer: number,
    inner: number,
    progress: number,
    telegraph: boolean,
    tint: number,
    alpha: number,
  ): void {
    const shape = view.shape;
    /* O limite exterior fica sempre visível: é a linha que se pode atravessar. */
    shape.lineStyle(3, TELEGRAPH_TINT, 0.55);
    shape.strokeCircle(ox, oy, outer);
    const current = telegraph ? outer : outer - (outer - inner) * progress;
    shape.lineStyle(6, tint, alpha + 0.25);
    shape.strokeCircle(ox, oy, current);
    shape.fillStyle(tint, alpha * 0.18);
    shape.fillCircle(ox, oy, current);
    /*
     * **As seis espadas orbitam o anel.** São seis imagens de UMA aplicação de
     * dano: o servidor cobra uma vez por casco, e a contagem visual não muda
     * isso nem é reduzida por causa disso.
     */
    for (let index = 0; index < JUDGMENT_SWORDS; index += 1) {
      const sword = view.swords[index];
      if (sword === undefined) {
        continue;
      }
      const spin = (index / JUDGMENT_SWORDS) * Math.PI * 2 + progress * Math.PI;
      sword.setVisible(true);
      sword.setPosition(ox + Math.cos(spin) * current, oy + Math.sin(spin) * current);
      sword.setTint(tint);
      sword.setAlpha(0.55 + progress * 0.4);
      sword.setDisplaySize(Math.max(6, outer * 0.09), Math.max(6, outer * 0.09));
    }
  }

  /**
   * O sector, desenhado pelo mesmo meio-ângulo que decide o acerto.
   *
   * `sectorTanBps` é a tangente em basis points; `Math.atan` traduz o número
   * autoritativo para o desenho, e o desenho é o único sítio onde ele aparece —
   * a decisão do servidor nunca passa por um ângulo.
   */
  private drawSector(
    shape: Phaser.GameObjects.Graphics,
    ox: number,
    oy: number,
    reach: number,
    angle: number,
    sectorTanBps: number,
    tint: number,
    alpha: number,
  ): void {
    const halfAngle = Math.atan(Math.max(1, sectorTanBps) / 10_000);
    shape.fillStyle(tint, alpha * 0.5);
    shape.slice(ox, oy, reach, angle - halfAngle, angle + halfAngle, false);
    shape.fillPath();
    shape.lineStyle(3, tint, alpha + 0.25);
    shape.beginPath();
    shape.arc(ox, oy, reach, angle - halfAngle, angle + halfAngle, false);
    shape.strokePath();
    shape.lineBetween(
      ox,
      oy,
      ox + Math.cos(angle - halfAngle) * reach,
      oy + Math.sin(angle - halfAngle) * reach,
    );
    shape.lineBetween(
      ox,
      oy,
      ox + Math.cos(angle + halfAngle) * reach,
      oy + Math.sin(angle + halfAngle) * reach,
    );
  }

  /**
   * **As cinco brasas de um rugido**, com o prazo que a sala lhes deu.
   *
   * A intensidade desce com o tempo que resta, medido contra o tique
   * autoritativo: uma brasa quase apagada vê-se que está quase apagada, e o
   * cliente nunca conta esse tempo por si.
   */
  private drawEmbers(
    embers: readonly BossEmberDraw[],
    tick: number,
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<number>();
    for (const ember of embers) {
      alive.add(ember.id);
      let view = this.embers.get(ember.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth - 1);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        const core = this.scene.add.image(0, 0, SPARK_TEXTURE);
        core.setDepth(this.depth - 1);
        core.setBlendMode(Phaser.BlendModes.ADD);
        view = { glow, core };
        this.embers.set(ember.id, view);
        /* Ela nasce com uma faísca, e a faísca sai da piscina partilhada. */
        this.burst(
          worldUnits(ember.x),
          worldUnits(ember.y),
          nowMs,
          ember.shape === 'sector' ? MIASMA_TINT.hot : EMBER_TINT,
        );
      }
      const total = Math.max(1, ember.expiresAtTick - ember.bornAtTick);
      const left = Math.max(0, Math.min(total, ember.expiresAtTick - tick));
      const fraction = left / total;
      const radius = worldUnits(ember.radiusMu);
      const x = worldUnits(ember.x);
      const y = worldUnits(ember.y);
      const dust = ember.slowBps > 0;
      const miasma = ember.shape === 'sector';
      /*
       * **A névoa é verde e translúcida; a brasa é laranja e densa.**
       *
       * A opacidade da névoa é deliberadamente baixa: ela nunca pode esconder
       * uma nave, um projétil ou o aviso de outra habilidade. Nada foi reduzido
       * por desempenho — é uma decisão de leitura.
       */
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(radius * 2, radius * 2);
      /*
       * **O pó aquece à medida que o prazo dele passa** (gate V28I.BE).
       *
       * Frio no princípio, incandescente no fim: o aviso da ignição é o próprio
       * pó a mudar de cor, e a fracção que o governa é o relógio AUTORITATIVO.
       * A lentidão vê-se porque a zona é visível, e não por um ícone.
       */
      view.glow.setTint(
        dust
          ? fraction < 0.25
            ? DUST_HOT_TINT
            : DUST_COLD_TINT
          : miasma
            ? MIASMA_TINT.warn
            : EMBER_TINT,
      );
      view.glow.setAlpha((miasma ? 0.12 : dust ? 0.16 : 0.22) + fraction * (miasma ? 0.2 : 0.4));
      view.core.setPosition(x, y);
      view.core.setDisplaySize(radius * (miasma ? 0.25 : 0.5), radius * (miasma ? 0.25 : 0.5));
      view.core.setTint(
        dust
          ? fraction < 0.25
            ? DUST_HOT_TINT
            : DUST_COLD_TINT
          : miasma
            ? MIASMA_TINT.hot
            : ACTIVE_TINT,
      );
      view.core.setAlpha((miasma ? 0.18 : 0.3) + fraction * 0.5);
    }
    for (const [id, view] of this.embers) {
      if (alive.has(id)) {
        continue;
      }
      view.glow.destroy();
      view.core.destroy();
      this.embers.delete(id);
    }
  }

  /**
   * **As esferas do enxame**, uma imagem por esfera e um rasto por esfera.
   *
   * Nunca uma imagem para várias, nunca um efeito agregado: elas são corpos
   * autoritativos contáveis, e o desenho conta-os. A piscina é a das vistas,
   * criada à entrada de cada esfera e destruída à saída dela.
   */
  private drawSwarm(
    swarm: readonly BossSwarmDraw[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<number>();
    for (const body of swarm) {
      alive.add(body.id);
      let view = this.swarm.get(body.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth + 2);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        const core = this.scene.add.image(0, 0, SPARK_TEXTURE);
        core.setDepth(this.depth + 3);
        core.setBlendMode(Phaser.BlendModes.ADD);
        view = { glow, core };
        this.swarm.set(body.id, view);
      }
      const x = worldUnits(body.x);
      const y = worldUnits(body.y);
      const radius = worldUnits(body.radiusMu);
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(radius * 4, radius * 4);
      view.glow.setTint(SWARM_HALO_TINT);
      view.glow.setAlpha(0.5);
      view.core.setPosition(x, y);
      view.core.setDisplaySize(radius * 2, radius * 2);
      view.core.setTint(SWARM_CORE_TINT);
      view.core.setAlpha(0.95);
      /* O rasto: uma faísca por tique de desenho, da piscina partilhada. */
      this.spawnSpark(x, y, 0, 0, nowMs, SWARM_HALO_TINT);
    }
    for (const [id, view] of this.swarm) {
      if (alive.has(id)) {
        continue;
      }
      /* Ela morreu ou saiu da vista: o impacto individual, e depois nada. */
      this.burst(view.core.x, view.core.y, nowMs, SWARM_CORE_TINT);
      view.glow.destroy();
      view.core.destroy();
      this.swarm.delete(id);
    }
  }

  /**
   * **As balas do Fogo Rotativo**: uma imagem e um rasto por bala.
   *
   * Vinte e cinco corpos são vinte e cinco desenhos. Nenhum representa vários,
   * nada é agrupado e nada é reduzido — o impacto de cada uma acontece quando
   * ela deixa a lista, que é quando ela morreu ou saiu da vista.
   */
  private drawRotary(
    rotary: readonly BossSwarmDraw[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<number>();
    for (const body of rotary) {
      alive.add(body.id);
      let view = this.rotary.get(body.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth + 2);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        const core = this.scene.add.image(0, 0, SPARK_TEXTURE);
        core.setDepth(this.depth + 3);
        core.setBlendMode(Phaser.BlendModes.ADD);
        view = { glow, core };
        this.rotary.set(body.id, view);
      }
      const x = worldUnits(body.x);
      const y = worldUnits(body.y);
      const radius = Math.max(2, worldUnits(body.radiusMu));
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(radius * 5, radius * 5);
      view.glow.setTint(ROTARY_HALO_TINT);
      view.glow.setAlpha(0.45);
      view.core.setPosition(x, y);
      view.core.setDisplaySize(radius * 2, radius * 2);
      view.core.setTint(ROTARY_CORE_TINT);
      view.core.setAlpha(0.95);
      this.spawnSpark(x, y, 0, 0, nowMs, ROTARY_HALO_TINT);
    }
    for (const [id, view] of this.rotary) {
      if (alive.has(id)) {
        continue;
      }
      this.burst(view.core.x, view.core.y, nowMs, ROTARY_CORE_TINT);
      view.glow.destroy();
      view.core.destroy();
      this.rotary.delete(id);
    }
  }

  /**
   * **Os obuses em voo**: núcleo, halo, rasto e uma linha até ao círculo onde
   * cai, por obus (gate V28I.CF.1A).
   *
   * Quatro obuses são quatro desenhos. Nenhum representa outro, nada é agrupado
   * e nada é escondido por distância ou densidade: o núcleo, o halo e a mira têm
   * mínimos no ecrã, e só o rasto — a camada decorativa — pode ficar subpixel.
   *
   * Nada aqui decide o impacto. O círculo cobra quando a sala o diz, e o corpo
   * sai no mesmo instante. Quando sai JUNTO do destino, acende-se o impacto no
   * centro do círculo; quando sai longe dele — o boss morreu, o bombardeio acabou
   * sem esse impacto — apaga-se sem explosão, porque não houve nenhuma.
   */
  private drawShellBodies(
    shells: readonly BossShellDraw[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    if (shells.length === 0 && this.shells.size === 0) {
      this.shellGuides?.clear();
      return;
    }
    const guides = this.shellGuides ?? this.makeShellGuides();
    guides.clear();
    const zoom = Math.max(0.01, this.scene.cameras.main.zoom);
    const coreLength = Math.max(
      SHELL_CORE_MIN_LENGTH_PX / zoom,
      PVP_HULL_WORLD_UNITS * SHELL_CORE_LENGTH_HULLS,
    );
    const coreWidth = Math.max(
      SHELL_CORE_MIN_WIDTH_PX / zoom,
      PVP_HULL_WORLD_UNITS * SHELL_CORE_WIDTH_HULLS,
    );
    const haloLength = Math.max(SHELL_HALO_MIN_LENGTH_PX / zoom, coreLength * SHELL_HALO_LENGTH);
    const haloWidth = Math.max(SHELL_HALO_MIN_WIDTH_PX / zoom, coreLength * SHELL_HALO_WIDTH);
    /* 0 no zoom 1 ou mais perto, 1 no mais afastado. */
    const farness = Math.max(0, Math.min(1, (1 - zoom) / (1 - SHELL_FARTHEST_ZOOM)));
    const haloAlpha = SHELL_HALO_ALPHA + SHELL_HALO_FAR_ALPHA * farness;
    const shadeAlpha =
      SHELL_SHADE_NEAR_ALPHA + (SHELL_SHADE_FAR_ALPHA - SHELL_SHADE_NEAR_ALPHA) * farness;
    const line = SHELL_GUIDE_SCREEN_PX / zoom;
    const mark = Math.max(SHELL_MARK_MIN_PX / zoom, coreLength * 0.8);
    const alive = this.shellsAlive;
    alive.clear();
    for (const body of shells) {
      alive.add(body.id);
      const x = body.drawnX;
      const y = body.drawnY;
      const targetX = worldUnits(body.targetX);
      const targetY = worldUnits(body.targetY);
      const dx = targetX - x;
      const dy = targetY - y;
      const distance = Math.hypot(dx, dy);
      let view = this.shells.get(body.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth + 2);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        /* Entre o halo e o núcleo: escurece o que está por trás do corpo, e só isso. */
        const shade = this.scene.add.image(0, 0, SPARK_TEXTURE);
        shade.setDepth(this.depth + 2.5);
        shade.setBlendMode(Phaser.BlendModes.NORMAL);
        const core = this.scene.add.image(0, 0, SPARK_TEXTURE);
        core.setDepth(this.depth + 3);
        core.setBlendMode(Phaser.BlendModes.ADD);
        view = {
          glow,
          shade,
          core,
          targetX,
          targetY,
          arrival: 0,
          rotation: Math.atan2(dy, dx),
          startDistance: Math.max(1, distance),
        };
        this.shells.set(body.id, view);
      }
      view.targetX = targetX;
      view.targetY = targetY;
      if (body.vx !== 0 || body.vy !== 0) {
        view.rotation = Math.atan2(body.vy, body.vx);
      }
      /* Dois passos e meio e o próprio corpo: a folga da interpolação no cursor. */
      view.arrival = worldUnits(Math.hypot(body.vx, body.vy) * 2.5) + coreLength;
      const cos = Math.cos(view.rotation);
      const sin = Math.sin(view.rotation);

      /* A energia: um halo frio, mais largo do que o núcleo e esticado no rumo. */
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(haloLength, haloWidth);
      view.glow.setRotation(view.rotation);
      view.glow.setTint(SHELL_HALO_TINT);
      view.glow.setAlpha(haloAlpha);
      /* A sombra: o contorno escuro que destaca o núcleo de uma arte clara por trás. */
      view.shade.setPosition(x, y);
      view.shade.setDisplaySize(coreLength * SHELL_SHADE_LENGTH, coreWidth * SHELL_SHADE_WIDTH);
      view.shade.setRotation(view.rotation);
      view.shade.setTint(SHELL_SHADE_TINT);
      view.shade.setAlpha(shadeAlpha);
      /* O núcleo: o pixel mais claro, curto e alongado no sentido do voo. */
      view.core.setPosition(x, y);
      view.core.setDisplaySize(coreLength, coreWidth);
      view.core.setRotation(view.rotation);
      view.core.setTint(SHELL_CORE_TINT);
      view.core.setAlpha(1);

      /*
       * A direcção e o destino, com pouco contraste. A linha apaga-se pela fracção
       * do caminho que falta e pára antes da mira, que fica até ao impacto.
       */
      const guideAlpha = SHELL_GUIDE_ALPHA * Math.min(1, distance / view.startDistance);
      const lineEnd = distance - mark;
      const lineStart = coreLength * 0.8;
      if (guideAlpha > 0.01 && lineEnd > lineStart) {
        guides.lineStyle(line, SHELL_GUIDE_TINT, guideAlpha);
        const span = lineEnd - lineStart;
        const dash = Math.max(SHELL_DASH_SCREEN_PX / zoom, span / (SHELL_DASHES_MAX * 2));
        const dashes = Math.min(SHELL_DASHES_MAX, Math.max(1, Math.floor(span / (dash * 2))));
        const ux = dx / distance;
        const uy = dy / distance;
        for (let step = 0; step < dashes; step += 1) {
          const from = lineStart + step * dash * 2;
          const to = Math.min(lineEnd, from + dash);
          guides.lineBetween(x + ux * from, y + uy * from, x + ux * to, y + uy * to);
        }
      }
      /* A mira: quatro braços com o centro livre, para o ponto do impacto ficar exacto. */
      guides.lineStyle(line, SHELL_GUIDE_TINT, SHELL_MARK_ALPHA);
      const gap = mark * 0.35;
      guides.lineBetween(targetX - mark, targetY, targetX - gap, targetY);
      guides.lineBetween(targetX + gap, targetY, targetX + mark, targetY);
      guides.lineBetween(targetX, targetY - mark, targetX, targetY - gap);
      guides.lineBetween(targetX, targetY + gap, targetX, targetY + mark);

      /* O resíduo: uma faísca pequena e fraca atrás da cauda, da piscina partilhada. */
      this.spawnSpark(
        x - cos * coreLength * 0.6,
        y - sin * coreLength * 0.6,
        0,
        0,
        nowMs,
        SHELL_TRAIL_TINT,
        { scale: SHELL_TRAIL_SCALE, alpha: SHELL_TRAIL_ALPHA, lifeMs: SHELL_TRAIL_LIFE_MS },
      );
    }
    for (const [id, view] of this.shells) {
      if (alive.has(id)) {
        continue;
      }
      const gap = Math.hypot(view.core.x - view.targetX, view.core.y - view.targetY);
      if (gap <= view.arrival) {
        this.shellImpact(view.targetX, view.targetY, view.rotation, nowMs, zoom);
      }
      view.glow.destroy();
      view.shade.destroy();
      view.core.destroy();
      this.shells.delete(id);
    }
  }

  private makeShellGuides(): Phaser.GameObjects.Graphics {
    const guides = this.scene.add.graphics();
    guides.setDepth(this.depth + 1);
    guides.setBlendMode(Phaser.BlendModes.ADD);
    this.shellGuides = guides;
    return guides;
  }

  /**
   * **Os quatro círculos do Bombardeio das Profundezas** (gate V28I.BG).
   *
   * Quatro, sempre quatro, e cada um no sítio que o servidor selou — eles vêm
   * em `points`, e não são recalculados aqui. Nenhum representa os outros,
   * nenhum é agrupado e nenhum é escondido por distância: quatro obuses são
   * quatro marcas e quatro explosões.
   *
   * Durante o voo os quatro estão marcados com a mesma intensidade. Na fase de
   * execução, a etapa diz qual deles rebenta agora — as etapas 2 a 5 são os
   * quatro impactos, um de cada vez — e esse acende, sem os outros apagarem.
   */
  private drawShells(
    shape: Phaser.GameObjects.Graphics,
    strike: BossStrikeDraw,
    reach: number,
    telegraph: boolean,
    tint: number,
    alpha: number,
    worldUnits: (mu: number) => number,
  ): void {
    const bursting = telegraph ? -1 : strike.stage - 2;
    const zoom = Math.max(0.01, this.scene.cameras.main.zoom);
    const edge = SHELL_CIRCLE_EDGE_PX / zoom;
    const soft = SHELL_CIRCLE_SOFT_PX / zoom;
    for (let shell = 0; shell * 2 + 1 < strike.points.length; shell += 1) {
      const cx = worldUnits(strike.points[shell * 2] ?? 0);
      const cy = worldUnits(strike.points[shell * 2 + 1] ?? 0);
      const hot = shell === bursting;
      /*
       * **Um preenchimento quase transparente.** Quatro círculos sobrepostos por
       * cima de um casco não o podem tapar, nem ao cenário: a área lê-se pela
       * borda, e o preenchimento só a tinge.
       */
      shape.fillStyle(tint, hot ? SHELL_CIRCLE_HOT_FILL_ALPHA : SHELL_CIRCLE_FILL_ALPHA);
      shape.fillCircle(cx, cy, reach);
      /* A borda suave: uma faixa larga e ténue por DENTRO, e o traço fino no raio exacto. */
      shape.lineStyle(soft, tint, hot ? SHELL_CIRCLE_SOFT_ALPHA * 2 : SHELL_CIRCLE_SOFT_ALPHA);
      shape.strokeCircle(cx, cy, Math.max(0, reach - soft / 2));
      shape.lineStyle(edge, tint, hot ? Math.min(1, alpha + 0.25) : alpha * 0.85);
      shape.strokeCircle(cx, cy, reach);
    }
  }

  /**
   * **As sete lâminas da Salva Coral** (gate V28I.BG).
   *
   * Sete linhas do corpo até aos sete pontos que o servidor selou, com a
   * meia-largura REAL de cada uma. As folgas entre elas são a resposta inteira a
   * esta habilidade, portanto desenhar um leque cheio seria mentir sobre a única
   * coisa que há para fazer.
   */
  private drawFeathers(
    shape: Phaser.GameObjects.Graphics,
    strike: BossStrikeDraw,
    ox: number,
    oy: number,
    half: number,
    tint: number,
    alpha: number,
    worldUnits: (mu: number) => number,
  ): void {
    const width = Math.max(2, half * 2);
    for (let line = 0; line * 2 + 1 < strike.points.length; line += 1) {
      const ex = worldUnits(strike.points[line * 2] ?? 0);
      const ey = worldUnits(strike.points[line * 2 + 1] ?? 0);
      shape.lineStyle(width, tint, alpha);
      shape.lineBetween(ox, oy, ex, ey);
      /* A ponta cravada: é o fim real da lâmina, e não uma decoração. */
      shape.fillStyle(tint, alpha + 0.2);
      shape.fillCircle(ex, ey, half);
    }
  }

  /**
   * **As cinco presas da Mordida da Matilha** (gate V28I.BG).
   *
   * O sector é a mecânica e já foi desenhado; estas cinco marcas ficam na BORDA
   * dele, uma por cabeça da montaria. São apresentação de UMA aplicação de dano
   * — o servidor cobra uma vez por casco, e a contagem visual não muda isso.
   */
  private drawFangs(
    shape: Phaser.GameObjects.Graphics,
    ox: number,
    oy: number,
    reach: number,
    angle: number,
    sectorTanBps: number,
    tint: number,
    alpha: number,
  ): void {
    const halfAngle = Math.atan(Math.max(1, sectorTanBps) / 10_000);
    const fang = Math.max(3, reach * 0.05);
    for (let index = 0; index < PACK_FANGS; index += 1) {
      const t = index / (PACK_FANGS - 1);
      const at = angle - halfAngle + halfAngle * 2 * t;
      shape.fillStyle(tint, alpha + 0.25);
      shape.fillCircle(ox + Math.cos(at) * reach, oy + Math.sin(at) * reach, fang);
    }
  }

  /**
   * **Os teleguiados, um corpo por identidade.**
   *
   * Um por alvo e por boss, e cada um é desenhado com o seu objeto, o seu rasto
   * e o seu impacto. Nenhum representa outro, nada é agrupado, e o impacto
   * acontece quando ele deixa a lista — que é quando acertou ou desapareceu.
   */
  private drawHoming(
    homing: readonly BossHomingDraw[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<number>();
    for (const body of homing) {
      alive.add(body.id);
      let view = this.homing.get(body.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth + 2);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        const core = this.scene.add.image(0, 0, SPARK_TEXTURE);
        core.setDepth(this.depth + 3);
        core.setBlendMode(Phaser.BlendModes.ADD);
        view = { glow, core };
        this.homing.set(body.id, view);
      }
      const palette = HOMING_TINT[body.ability];
      const x = worldUnits(body.x);
      const y = worldUnits(body.y);
      const radius = Math.max(3, worldUnits(body.radiusMu));
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(radius * 5, radius * 5);
      view.glow.setTint(palette.warn);
      view.glow.setAlpha(0.55);
      view.core.setPosition(x, y);
      /* Alongado no rumo: um arpão, uma lança e um olhar apontam para onde vão. */
      view.core.setDisplaySize(radius * 3.4, radius * 1.6);
      view.core.setRotation(Math.atan2(body.dirY, body.dirX));
      view.core.setTint(palette.hot);
      view.core.setAlpha(0.95);
      this.spawnSpark(x, y, 0, 0, nowMs, palette.warn);
    }
    for (const [id, view] of this.homing) {
      if (alive.has(id)) {
        continue;
      }
      this.burst(view.core.x, view.core.y, nowMs, view.core.tintTopLeft);
      view.glow.destroy();
      view.core.destroy();
      this.homing.delete(id);
    }
  }

  /**
   * **Os núcleos rúnicos**: dois por Sentença, com a vida de cada um.
   *
   * A barra é a vida que a autoridade diz, e não uma estimativa: partir um
   * núcleo é a mecânica inteira desta ultimate, e um jogador tem de ver quanto
   * falta. Quando um sai da lista foi partido ou expirou, e rebenta.
   */
  private drawCores(
    cores: readonly BossCoreDraw[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<number>();
    for (const core of cores) {
      alive.add(core.id);
      let view = this.cores.get(core.id);
      if (view === undefined) {
        const glow = this.scene.add.image(0, 0, GLOW_TEXTURE);
        glow.setDepth(this.depth + 1);
        glow.setBlendMode(Phaser.BlendModes.ADD);
        const stone = this.scene.add.image(0, 0, SPARK_TEXTURE);
        stone.setDepth(this.depth + 2);
        stone.setBlendMode(Phaser.BlendModes.ADD);
        const bar = this.scene.add.graphics();
        bar.setDepth(this.depth + 3);
        view = { glow, stone, bar };
        this.cores.set(core.id, view);
      }
      const x = worldUnits(core.x);
      const y = worldUnits(core.y);
      const radius = Math.max(4, worldUnits(core.radiusMu));
      view.glow.setPosition(x, y);
      view.glow.setDisplaySize(radius * 3, radius * 3);
      view.glow.setTint(CORE_TINT);
      view.glow.setAlpha(0.5);
      view.stone.setPosition(x, y);
      view.stone.setDisplaySize(radius * 2, radius * 2);
      view.stone.setTint(CORE_TINT);
      view.stone.setAlpha(0.95);
      const max = BigInt(core.maxHealthMilli);
      const current = BigInt(core.healthMilli);
      const fraction = max <= 0n ? 0 : Number((current * 1000n) / max) / 1000;
      const width = radius * 2.4;
      view.bar.clear();
      view.bar.setPosition(x, y);
      view.bar.fillStyle(0x1a0a0f, 0.8);
      view.bar.fillRect(-width / 2, -radius - 10, width, 5);
      view.bar.fillStyle(CORE_BAR_TINT, 1);
      view.bar.fillRect(-width / 2, -radius - 10, width * Math.max(0, Math.min(1, fraction)), 5);
    }
    for (const [id, view] of this.cores) {
      if (alive.has(id)) {
        continue;
      }
      this.burst(view.stone.x, view.stone.y, nowMs, CORE_TINT);
      view.glow.destroy();
      view.stone.destroy();
      view.bar.destroy();
      this.cores.delete(id);
    }
  }

  /**
   * **O aviso de uma ultimate**, e a saída que ele anuncia.
   *
   * O Decreto desenha o ANEL autoritativo — as duas bordas vêm da sala, e a
   * banda entre elas é exactamente a que magoa. As outras duas desenham o corpo
   * a canalizar e o progresso da interrupção, que é o que diz se o trabalho
   * está a chegar a tempo.
   *
   * Quem não está envolvido continua a ver tudo, com menos intensidade: a luta
   * é dos outros também, e esconder-lha seria esconder o que está a acontecer.
   */
  private drawUltimates(
    ultimates: readonly BossUltimateDraw[],
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();
    for (const one of ultimates) {
      alive.add(one.id);
      let shape = this.ultimates.get(one.id);
      if (shape === undefined) {
        shape = this.scene.add.graphics();
        shape.setDepth(this.depth);
        shape.setBlendMode(Phaser.BlendModes.ADD);
        this.ultimates.set(one.id, shape);
      }
      shape.clear();
      const palette = ULTIMATE_TINT[one.ability];
      const telegraph = one.phase === 'telegraph';
      const tint = telegraph ? palette.warn : palette.hot;
      const progress = Math.max(0, Math.min(1, one.progressMilli / 1_000));
      const alpha = (one.involved ? 0.34 : 0.18) + progress * 0.3;
      const x = worldUnits(one.x);
      const y = worldUnits(one.y);
      const body = worldUnits(one.radiusMu);

      if (one.outerMu > 0) {
        /*
         * **O anel do Decreto**: a banda entre as duas bordas é a que magoa, e
         * o que está dentro do corpo e fora da borda de fora é a saída. As duas
         * linhas são as fronteiras reais, e não uma aproximação delas.
         */
        const inner = worldUnits(one.innerMu);
        const outer = worldUnits(one.outerMu);
        shape.fillStyle(tint, alpha * 0.5);
        shape.beginPath();
        shape.arc(x, y, outer, 0, Math.PI * 2, false);
        shape.arc(x, y, inner, 0, Math.PI * 2, true);
        shape.closePath();
        shape.fillPath();
        shape.lineStyle(4, tint, alpha + 0.35);
        shape.strokeCircle(x, y, inner);
        shape.strokeCircle(x, y, outer);
      } else {
        /* A canalização: um halo à volta do corpo, que aperta com o tempo. */
        shape.lineStyle(6, tint, alpha + 0.25);
        shape.strokeCircle(x, y, body * (1.9 - progress * 0.6));
        shape.fillStyle(tint, alpha * 0.25);
        shape.fillCircle(x, y, body * 1.2);
      }

      if (one.interruptMilli > 0) {
        /*
         * **O progresso da interrupção**, em milésimos e vindo da autoridade:
         * um arco que fecha à volta do corpo. Ele diz que o trabalho está a
         * contar, e é a única leitura que existe do limiar — o número absoluto
         * é do servidor e não viaja.
         */
        const done = Math.max(0, Math.min(1, one.interruptMilli / 1_000));
        shape.lineStyle(5, ULTIMATE_TINT[one.ability].hot, 0.85);
        shape.beginPath();
        shape.arc(x, y, body * 1.45, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * done, false);
        shape.strokePath();
      }
    }
    for (const [id, shape] of this.ultimates) {
      if (alive.has(id)) {
        continue;
      }
      shape.destroy();
      this.ultimates.delete(id);
    }
  }

  /**
   * **O impacto de um obus**: o momento mais forte do bombardeio, e curto.
   *
   * Um clarão no ponto exacto, uma onda pequena e poucos fragmentos que se
   * dissipam depressa. Na camada do círculo, por BAIXO do casco e do boss, e do
   * tamanho de um casco e não do círculo: nunca tapa uma nave, uma barra ou outro
   * aviso, e a área do dano já está desenhada pelo círculo.
   *
   * Da piscina, com tecto. Cheia, recicla o impacto mais velho.
   */
  private shellImpact(x: number, y: number, rotation: number, nowMs: number, zoom: number): void {
    let impact = this.impacts.find((one) => nowMs - one.bornMs >= SHELL_WAVE_MS);
    if (impact === undefined && this.impacts.length < SHELL_IMPACT_MAX) {
      const flash = this.scene.add.image(0, 0, GLOW_TEXTURE);
      const spark = this.scene.add.image(0, 0, SPARK_TEXTURE);
      const wave = this.scene.add.image(0, 0, RING_TEXTURE);
      for (const image of [flash, spark, wave]) {
        image.setDepth(this.depth);
        image.setBlendMode(Phaser.BlendModes.ADD);
        image.setVisible(false);
      }
      impact = { flash, spark, wave, bornMs: Number.NEGATIVE_INFINITY, flashSize: 0, waveSize: 0 };
      this.impacts.push(impact);
    }
    impact ??= this.impacts.reduce((oldest, one) => (one.bornMs < oldest.bornMs ? one : oldest));
    impact.bornMs = nowMs;
    impact.flashSize = Math.max(
      SHELL_FLASH_MIN_PX / zoom,
      PVP_HULL_WORLD_UNITS * SHELL_FLASH_HULLS,
    );
    impact.waveSize = Math.max(SHELL_WAVE_MIN_PX / zoom, PVP_HULL_WORLD_UNITS * SHELL_WAVE_HULLS);
    impact.flash.setPosition(x, y);
    impact.flash.setTint(SHELL_HALO_TINT);
    impact.spark.setPosition(x, y);
    impact.spark.setTint(SHELL_CORE_TINT);
    impact.wave.setPosition(x, y);
    impact.wave.setTint(SHELL_HALO_TINT);
    this.stepImpact(impact, nowMs);
    /* Poucos fragmentos, em leque regular a partir do rumo: nada de aleatório. */
    const speed = (impact.waveSize * 0.9) / (SHELL_FRAGMENT_LIFE_MS / 1000);
    for (let index = 0; index < SHELL_FRAGMENTS; index += 1) {
      const angle = rotation + (index * Math.PI * 2) / SHELL_FRAGMENTS;
      this.spawnSpark(
        x,
        y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        nowMs,
        SHELL_CORE_TINT,
        { scale: 0.7, alpha: 0.9, lifeMs: SHELL_FRAGMENT_LIFE_MS, depth: this.depth },
      );
    }
  }

  private stepImpacts(nowMs: number): void {
    for (const impact of this.impacts) {
      this.stepImpact(impact, nowMs);
    }
  }

  /** Um impacto no tempo: o clarão apaga-se em 150 ms, a onda abre e perde força em 260. */
  private stepImpact(impact: ImpactView, nowMs: number): void {
    const age = nowMs - impact.bornMs;
    if (age >= SHELL_WAVE_MS) {
      impact.flash.setVisible(false);
      impact.spark.setVisible(false);
      impact.wave.setVisible(false);
      return;
    }
    if (age < SHELL_FLASH_MS) {
      const fade = 1 - age / SHELL_FLASH_MS;
      const flashSize = impact.flashSize * (0.75 + 0.25 * fade);
      impact.flash.setVisible(true);
      impact.flash.setDisplaySize(flashSize, flashSize);
      impact.flash.setAlpha(0.85 * fade * fade);
      impact.spark.setVisible(true);
      impact.spark.setDisplaySize(impact.flashSize * 0.32, impact.flashSize * 0.32);
      impact.spark.setAlpha(fade);
    } else {
      impact.flash.setVisible(false);
      impact.spark.setVisible(false);
    }
    const opened = age / SHELL_WAVE_MS;
    const eased = 1 - (1 - opened) * (1 - opened);
    const waveSize = impact.waveSize * (0.25 + 0.75 * eased);
    impact.wave.setVisible(true);
    impact.wave.setDisplaySize(waveSize, waveSize);
    impact.wave.setAlpha(0.7 * (1 - opened));
  }

  /** Um punhado de faíscas, da piscina. Nunca uma alocação dentro de um quadro. */
  private burst(x: number, y: number, nowMs: number, tint: number): void {
    const count = 10;
    for (let index = 0; index < count; index += 1) {
      this.scatter = (this.scatter + 1) % 360;
      const angle = (this.scatter * Math.PI) / 180 + index;
      const speed = 40 + (index % 5) * 22;
      this.spawnSpark(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, nowMs, tint);
    }
  }

  private spawnSpark(
    x: number,
    y: number,
    vx: number,
    vy: number,
    nowMs: number,
    tint: number,
    style?: SparkStyle,
  ): void {
    const reused = this.sparks.find((spark) => nowMs - spark.bornMs >= spark.lifeMs);
    const spark =
      reused ??
      (this.sparks.length < SPARK_MAX
        ? this.newSpark()
        : /* A piscina está cheia: recicla a mais velha, e nunca aloca uma nova. */
          this.sparks.reduce((oldest, one) => (one.bornMs < oldest.bornMs ? one : oldest)));
    spark.bornMs = nowMs;
    spark.vx = vx;
    spark.vy = vy;
    spark.fromScale = style?.scale ?? 1;
    spark.toScale = (style?.scale ?? 1) * 0.2;
    spark.tint = tint;
    spark.lifeMs = style?.lifeMs ?? SPARK_LIFE_MS;
    spark.alpha = style?.alpha ?? 1;
    spark.image.setDepth(style?.depth ?? this.depth + 2);
    spark.image.setPosition(x, y);
    spark.image.setTint(tint);
    spark.image.setAlpha(spark.alpha);
    spark.image.setVisible(true);
  }

  private newSpark(): Spark {
    const image = this.scene.add.image(0, 0, SPARK_TEXTURE);
    image.setDepth(this.depth + 2);
    image.setBlendMode(Phaser.BlendModes.ADD);
    image.setVisible(false);
    const spark: Spark = {
      image,
      bornMs: 0,
      vx: 0,
      vy: 0,
      fromScale: 1,
      toScale: 0.2,
      tint: ACTIVE_TINT,
      lifeMs: SPARK_LIFE_MS,
      alpha: 1,
    };
    this.sparks.push(spark);
    return spark;
  }

  private stepSparks(nowMs: number): void {
    for (const spark of this.sparks) {
      const age = nowMs - spark.bornMs;
      if (age >= spark.lifeMs) {
        spark.image.setVisible(false);
        continue;
      }
      const fraction = age / spark.lifeMs;
      const scale = spark.fromScale + (spark.toScale - spark.fromScale) * fraction;
      spark.image.setPosition(spark.image.x + spark.vx * 0.016, spark.image.y + spark.vy * 0.016);
      spark.image.setDisplaySize(SPARK_PX * scale, SPARK_PX * scale);
      spark.image.setAlpha(spark.alpha * (1 - fraction));
    }
    /* O tecto das brasas decorativas nunca é ultrapassado por acumulação. */
    if (this.sparks.length > EMBER_MOTE_MAX + SPARK_MAX) {
      const extra = this.sparks.splice(0, this.sparks.length - SPARK_MAX);
      for (const spark of extra) {
        spark.image.destroy();
      }
    }
  }
}
